/**
 * Demo data for Pelmeshka GitHub Pages static version.
 * Contains 12 months of realistic Dutch spending patterns (ABN AMRO + Credit Agricole)
 * and a sample budget for BAF/Reporting.
 * 
 * This is seeded into localStorage on first visit when backend is unavailable.
 */

// Default categories that ship with the app
const DEFAULT_CATEGORIES = [
  'Banking',
  'Business Expense',
  'Coffee',
  'Food',
  'Fuel',
  'Gifts',
  'Health Insurance',
  'Healthcare',
  'Holiday',
  'Home Internet',
  'Home Maintenance',
  'Hotel',
  'Income Tax',
  'Interests',
  'Movie',
  'Not an expense',
  'Other',
  'Personal Phone',
  'Public Transport',
  'Restaurant',
  'Salary',
  'Savings',
  'Sports',
  'Transfers',
  'Transport',
  'Utilities'
];

// Category hierarchy mapping (used for divisions display)
const CATEGORY_HIERARCHY = {
  'Banking': {
    division: '12 - Miscellaneous goods and services',
    group: '12.5 - Financial services n.e.c.',
    class: 'Banking'
  },
  'Business Expense': {
    division: '12 - Miscellaneous goods and services',
    group: '30.4 - Work related expenses',
    class: 'Business Expense'
  },
  'Coffee': {
    division: '01 - Food and non-alcoholic beverages',
    group: '01.2 - Non-alcoholic beverages',
    class: 'Coffee'
  },
  'Food': {
    division: '01 - Food and non-alcoholic beverages',
    group: '01.1 - Food',
    class: 'Food'
  },
  'Fuel': {
    division: '07 - Transport',
    group: '07.2 - Operation of personal transport equipment',
    class: 'Fuel'
  },
  'Gifts': {
    division: '40 - Voluntary Transfers',
    group: '40.2 - Gifts',
    class: 'Gifts'
  },
  'Health Insurance': {
    division: '06 - Health',
    group: '06.1 - Medical products, appliances and equipment',
    class: 'Health Insurance'
  },
  'Healthcare': {
    division: '06 - Health',
    group: '06.1 - Medical products, appliances and equipment',
    class: 'Healthcare'
  },
  'Holiday': {
    division: '11 - Restaurants and hotels',
    group: '11.2 - Accommodation services',
    class: 'Holiday'
  },
  'Home Internet': {
    division: '08 - Communication',
    group: '08.2 - Telephone and telefax equipment',
    class: 'Home Internet'
  },
  'Home Maintenance': {
    division: '04 - Housing, water, electricity, gas and other fuels',
    group: '04.3 - Maintenance and repair of the dwelling',
    class: 'Home Maintenance'
  },
  'Hotel': {
    division: '11 - Restaurants and hotels',
    group: '11.2 - Accommodation services',
    class: 'Hotel'
  },
  'Income Tax': {
    division: '20 - Taxes and Compulsory Transfers',
    group: '20.1 - Income tax',
    class: 'Income Tax'
  },
  'Interests': {
    division: '50 - Income',
    group: '50.3 - Investment income',
    class: 'Interests'
  },
  'Movie': {
    division: '09 - Recreation, sport and culture',
    group: '09.4 - Recreational and cultural services',
    class: 'Movie'
  },
  'Not an expense': {
    division: '99 - Not an expense Division',
    group: '99.1 - Not an expense Group',
    class: 'Not an expense'
  },
  'Other': {
    division: '12 - MIscellaneous goods and services Division',
    group: '12.6 - Other services n.e.c Group',
    class: 'Other'
  },
  'Personal Phone': {
    division: '08 - Communication',
    group: '08.3 - Telephone and telefax services',
    class: 'Personal Phone'
  },
  'Public Transport': {
    division: '07 - Transport',
    group: '07.3 - Transport services',
    class: 'Public Transport'
  },
  'Restaurant': {
    division: '11 - Restaurants and hotels',
    group: '11.1 - Catering services',
    class: 'Restaurant'
  },
  'Salary': {
    division: '50 - Income',
    group: '50.1 - Employment income',
    class: 'Salary'
  },
  'Savings': {
    division: '30 - Financial Transactions',
    group: '30.3 - Savings and investments',
    class: 'Savings'
  },
  'Sports': {
    division: '09 - Recreation, sport and culture',
    group: '09.2 - Other major durables for recreation and culture',
    class: 'Sports'
  },
  'Transfers': {
    division: '99 - Not an expense Division',
    group: '99.1 - Not an expense Group',
    class: 'Transfers'
  },
  'Transport': {
    division: '07 - Transport',
    group: '07.3 - Transport services',
    class: 'Transport'
  },
  'Utilities': {
    division: '04 - Housing, water, electricity, gas and other fuels',
    group: '04.5 - Electricity, gas and other fuels',
    class: 'Utilities'
  }
};

/**
 * Generate realistic transaction date in format YYYY-MM-DD
 */
function generateTransactionDate(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Generate demo transactions for 12 months
 */
function generateDemoTransactions() {
  const transactions = [];
  let txId = 1;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  
  // Start 12 months ago
  let year = currentMonth > 1 ? currentYear : currentYear - 1;
  let month = currentMonth > 1 ? currentMonth - 12 : currentMonth;
  if (month <= 0) month += 12;
  if (month > 12) {
    month -= 12;
    year += 1;
  }

  // Helper to add transaction
  const addTx = (txDate, description, amount, category, source, isIncome = false) => {
    const hierarchy = CATEGORY_HIERARCHY[category] || CATEGORY_HIERARCHY['Other'];
    transactions.push({
      _id: `demo_${txId}`,
      id: `demo_${txId}`,
      accountnumber: source === 'ABN AMRO' ? 'NL91ABNA0417164300' : 'FR1420041010050500013M02606',
      currency: 'EUR',
      transactiondate: txDate,
      interestdate: txDate,
      startamount: 0,
      endamount: 0,
      amount: isIncome ? Math.abs(amount) : -Math.abs(amount),
      original_amount: isIncome ? Math.abs(amount) : -Math.abs(amount),
      description: description,
      date_added: new Date().toISOString(),
      unique_key: `demo_${txId}_${txDate}`,
      auto_exclude: 0,
      manual_exclude: 0,
      exclude: 0,
      manual_category: null,
      classification: null,
      source: source,
      division: hierarchy.division,
      group_name: hierarchy.group,
      class_name: hierarchy.class,
      final_category: category,
      revolut_type: null,
      revolut_fee: null,
      revolut_state: null,
      revolut_balance: null,
      fix_variable: 'Variable'
    });
    txId += 1;
  };

  // Generate 12 months of transactions
  for (let m = 0; m < 12; m++) {
    const txMonth = month + m;
    const txYear = txMonth > 12 ? year + 1 : year;
    const displayMonth = txMonth > 12 ? txMonth - 12 : txMonth;

    // Salary (first of month, ABN AMRO)
    addTx(generateTransactionDate(txYear, displayMonth, 1), 'Employer Salary Transfer', 3200, 'Salary', 'ABN AMRO', true);

    // Rent (5th of month, ABN AMRO)
    addTx(generateTransactionDate(txYear, displayMonth, 5), 'Rent Payment - Landlord', 1300, 'Transfers', 'ABN AMRO');

    // Utilities (10th, ABN AMRO)
    addTx(generateTransactionDate(txYear, displayMonth, 10), 'Eneco Gas & Electricity', 185, 'Utilities', 'ABN AMRO');

    // Home Internet (12th, ABN AMRO)
    addTx(generateTransactionDate(txYear, displayMonth, 12), 'KPN Home Internet Monthly', 60, 'Home Internet', 'ABN AMRO');

    // Groceries (random days, ABN AMRO)
    [3, 8, 15, 22, 29].forEach(day => {
      if (day <= 28) {
        const amount = 50 + Math.random() * 80;
        addTx(generateTransactionDate(txYear, displayMonth, day), 'Albert Heijn Groceries', amount, 'Food', 'ABN AMRO');
      }
    });

    // Coffee/Coffee shops (random)
    [7, 14, 21, 28].forEach(day => {
      if (day <= 28) {
        const amount = 4 + Math.random() * 4;
        addTx(generateTransactionDate(txYear, displayMonth, day), 'Local Coffee Shop', amount, 'Coffee', 'ABN AMRO');
      }
    });

    // Restaurants (random weekends)
    [11, 18, 25].forEach(day => {
      if (day <= 28) {
        const amount = 35 + Math.random() * 40;
        addTx(generateTransactionDate(txYear, displayMonth, day), 'Restaurant Dinner', amount, 'Restaurant', 'ABN AMRO');
      }
    });

    // Personal Phone (15th, ABN AMRO)
    addTx(generateTransactionDate(txYear, displayMonth, 15), 'T-Mobile Mobile Plan', 35, 'Personal Phone', 'ABN AMRO');

    // Public Transport (monthly, ABN AMRO)
    addTx(generateTransactionDate(txYear, displayMonth, 2), 'GVB Amsterdam Transit Card', 105, 'Public Transport', 'ABN AMRO');

    // Fuel (random days if needed, Credit Agricole)
    if (Math.random() > 0.4) {
      const fuelDay = 8 + Math.floor(Math.random() * 15);
      if (fuelDay <= 28) {
        const amount = 45 + Math.random() * 35;
        addTx(generateTransactionDate(txYear, displayMonth, fuelDay), 'Shell Gas Station Fuel', amount, 'Fuel', 'Credit Agricole');
      }
    }

    // Gym/Sports (10th, Credit Agricole)
    addTx(generateTransactionDate(txYear, displayMonth, 10), 'Fitness First Membership', 45, 'Sports', 'Credit Agricole');

    // Movie/Entertainment (random, Credit Agricole)
    if (Math.random() > 0.5) {
      const movieDay = 6 + Math.floor(Math.random() * 20);
      if (movieDay <= 28) {
        const amount = 10 + Math.random() * 8;
        addTx(generateTransactionDate(txYear, displayMonth, movieDay), 'Netflix Monthly', amount, 'Movie', 'Credit Agricole');
      }
    }

    // Shopping/Other (random, Credit Agricole)
    if (Math.random() > 0.6) {
      const shoppingDay = 13 + Math.floor(Math.random() * 12);
      if (shoppingDay <= 28) {
        const amount = 30 + Math.random() * 70;
        addTx(generateTransactionDate(txYear, displayMonth, shoppingDay), 'Clothing Store Purchase', amount, 'Other', 'Credit Agricole');
      }
    }

    // Healthcare (random, Credit Agricole)
    if (Math.random() > 0.8) {
      const healthDay = 5 + Math.floor(Math.random() * 20);
      if (healthDay <= 28) {
        const amount = 25 + Math.random() * 50;
        addTx(generateTransactionDate(txYear, displayMonth, healthDay), 'Pharmacy Purchase', amount, 'Healthcare', 'Credit Agricole');
      }
    }

    // Savings transfer (end of month, ABN AMRO)
    if (Math.random() > 0.5) {
      addTx(generateTransactionDate(txYear, displayMonth, 28), 'Transfer to Savings Account', 200, 'Savings', 'ABN AMRO');
    }

    // Occasional gift (random month)
    if (Math.random() > 0.9) {
      const giftDay = 5 + Math.floor(Math.random() * 20);
      if (giftDay <= 28) {
        const amount = 20 + Math.random() * 40;
        addTx(generateTransactionDate(txYear, displayMonth, giftDay), 'Birthday Gift Purchase', amount, 'Gifts', 'ABN AMRO');
      }
    }

    // Vacation/Holiday expense (random in summer months)
    if ((displayMonth === 7 || displayMonth === 8) && Math.random() > 0.7) {
      const holidayDay = 10 + Math.floor(Math.random() * 12);
      const amount = 200 + Math.random() * 400;
      addTx(generateTransactionDate(txYear, displayMonth, holidayDay), 'Booking.com Holiday Hotel', amount, 'Holiday', 'Credit Agricole');
    }
  }

  return transactions;
}

/**
 * Generate demo budget aligned with realistic Dutch spending
 */
function generateDemoBudget() {
  const budget = [
    { category: 'Food', amount: 400 },
    { category: 'Restaurant', amount: 150 },
    { category: 'Coffee', amount: 50 },
    { category: 'Utilities', amount: 200 },
    { category: 'Public Transport', amount: 120 },
    { category: 'Personal Phone', amount: 50 },
    { category: 'Home Internet', amount: 80 },
    { category: 'Sports', amount: 60 },
    { category: 'Movie', amount: 50 },
    { category: 'Healthcare', amount: 100 },
    { category: 'Fuel', amount: 150 },
    { category: 'Other', amount: 200 },
    { category: 'Gifts', amount: 50 },
    { category: 'Business Expense', amount: 100 }
  ];

  return budget;
}

/**
 * Public export: demo transactions and budget
 */
export const DEMO_DATA = {
  transactions: generateDemoTransactions(),
  budget: generateDemoBudget()
};

export { DEFAULT_CATEGORIES, CATEGORY_HIERARCHY };
